if(!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    // helper functions
    hideFormElement(element) {
      /*
        If the element is found in the DOM, and does not have the hide class, add it.
      */
      if(element && !element.classList.contains('hide')) {
        element.classList.add('hide');
      }
    }

    showFormElement(element) {
      /*
        If the element is found in the DOM, and it has the hide class, remove it it.
      */
      if(element && element.classList.contains('hide')) {
        element.classList.remove('hide');
      }
    }

    // init functions
    init(){
      // used forform reversion, if this is set to true we do not want to open forms upon radio change.
      this.formReverting = false;
      this.defaultVariant = this.dataset.defaultVariant;
      // card elements
      this.cardPrice = this.querySelector('.product-card__price .price .price__regular'); // there is a chance for this to go awry if this element isn't present.
      // form elements
      this.form = this.querySelector('form.product-card-form');
      this.skuInput = this.querySelector('input[name="sku"]');
      this.variantSelect = this.querySelector('select[name="id"]');
      this.sellingPlanSelect = this.querySelector('select[name="selling_plan"]');

      // form layouts
      this.miniForm = this.querySelector('.mini-form');
      this.formTop = this.querySelector('.product-card__form-top');
      this.formBottom = this.querySelector('.product-card__form-bottom');

      // interactive form elements
      this.submitButtons = this.querySelectorAll('button[type="submit"]');
      this.selectOptionsButton = this.querySelector('button.product-card__button-select-options');
      this.productOptionsContainer = this.querySelector('.product-options__container');

      this.variantsContainer = this.querySelector('.product-options__variants');
      this.variantRadios = this.querySelectorAll('.product-card__option input');

      this.sellingPlanContainer = this.querySelector('.product-options__selling-plans');
      this.sellingPlanRadios = this.querySelectorAll('.product-card__selling-plan input');

      this.formClose = this.querySelectorAll('.rc_plp_close_qv');

      // inits
      this.initEventListeners();
    }

    initEventListeners(){
      /*
        This function will set up event listeners for the component instance. Check for the existence of certain elements before creating the listeners.
      */
      // variant change event
      let wasCheckedVariant = null; // stores the last checked variant radio

      if(this.variantRadios){
        this.variantRadios.forEach((variantRadio)=>{
          variantRadio.addEventListener("change", (event)=>{
            const triggerElement = event.target;
            this.handleVariantChange(triggerElement);
            // if the event trigger was from the bottom form, open the top form.
            const bottomForm = triggerElement.closest('.product-card__form-bottom');
            if(bottomForm && (!this.formReverting)){
              this.openFormTop();
            }
            if(triggerElement.checked){
              wasCheckedVariant = triggerElement; // update the last checked variant
            }
          });
          variantRadio.addEventListener("click", (event)=>{
            const triggerElement = event.target;
            const bottomForm = triggerElement.closest('.product-card__form-bottom');
            // if the event trigger was from the bottom form, and the radio was already checked, close the top form.
            if(bottomForm && wasCheckedVariant == triggerElement){
              this.closeFormTop();
              this.revertForms(); // revert to reset the selected variant to the default.
            }
          });
        });
      }

      // selling plan change event
      if(this.sellingPlanRadios){
        this.sellingPlanRadios.forEach((sellingPlanRadio)=>{
          sellingPlanRadio.addEventListener("change", (event)=>{
            event.preventDefault();
            this.handleSubscriptionChange(event.target);
          });
        });
      }

      // "select options" click event.
      if(this.selectOptionsButton){
        this.selectOptionsButton.addEventListener("click", (event)=>{
          // this element is only found in the mini-form, so we will trust opening the mini form is the right move.
          this.openMiniForm();
        });
      }

      // form close click event
      if(this.formClose){
        this.formClose.forEach((close)=>{
          close.addEventListener("click", (event)=>{
            event.preventDefault();
            this.closeAllForms();
            this.revertForms();
          });
        });
      }

      // "add to cart" event.
      if(this.form){
        this.form.addEventListener("submit", (event)=>{
          event.preventDefault();
          this.handleAddToCart();
        });
      }
    }

    // event functions
    async handleAddToCart(){
      // console.log(`handleAddToCart`);
      if(!this.form) return;
      // format the form into Shopify's preferred format for the AJAX Cart API.
      let formData = new FormData(this.form);
      const formParams = new URLSearchParams(formData);
      const tempObject = Object.fromEntries(formParams);
      let formDataJSON = {};
      const accepted_keys = ['id', 'quantity', 'properties', 'selling_plan']
      for (let key in tempObject) {
        // check if key starts with one of the accepted keys and the value is not empty
        if (accepted_keys.some(accepted_key => key.startsWith(accepted_key)) && tempObject[key] !== "") {
          if (key.startsWith('properties')) {
            if (!formDataJSON['properties']) {
              formDataJSON['properties'] = {};
            }
            let innerKey = key.split('[')[1].split(']')[0];
            // check if value is not empty
            if (tempObject[key] !== "") {
              formDataJSON['properties'][innerKey] = tempObject[key];
            }
          } else {
            formDataJSON[key] = tempObject[key];
          }
        }
      }

      const items_added = [formDataJSON];
      const sendData = { items: items_added };

      // send data to Shopify API
      const url = '/cart/add.js';
      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sendData)
      };
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        } else {
          const data = await response.json();
          document.dispatchEvent(new CustomEvent("cart:item:added", { detail:
            {
              'response': data,
              'items_added': items_added
            }
          }));
          // close product forms.
          this.closeAllForms();
          // revert the forms
          this.revertForms();
          // summon the cart drawer.
          window.theme.CartDrawer.refresh().then(()=>{
            window.theme.CartDrawer.handleGWP();
            window.theme.CartDrawer.open();
          });
        }
      } catch (error) {
        console.log(error);
      }
    }

    revertForms(){
      // console.log(`ProductCard revertForms()`);
      // reset selection forms to the most default options.
      this.formReverting = true;
      this.resetVariants();
      this.resetSubscriptions();
      this.formReverting = false;
    }

    resetVariants() {
      // reset to the default variant, and then visually uncheck the radios.
      // console.log(`ProductCard resetVariants()`);
      [...this.variantRadios].forEach((radio) => {
        if(radio.dataset.variantId == this.defaultVariant){
          radio.click();
        }
        // visually uncheck the radios.
        radio.checked = false;
      });
    }

    resetSubscriptions() {
      // reset the selected subscription to "onetime"
      // console.log(`ProductCard resetSubscriptions()`);
      [...this.sellingPlanRadios].forEach((radio) => {
        if(radio.value == "onetime"){
          radio.click();
        }
      });
    }

    handleVariantChange(input){
      // console.log(`ProductCard handleVariantChange()`, input);
      /*
        This function will handle updating the value of the variantSelect element.
        The event listener will send the event target to this function, search for an <option> element in the variantSelect that matches the variant ID of the event target.
        If we can successfully find a variant, we want to update the SKU input (legacy code).
        If this product has selling plans, and the current selected selling plan value isn't "" aka onetime, update the selling plan select to best match the current selected variant.
        If the product does not have selling plans, update the cart button.
      */
      if(!input) return;
      const variantId = input.dataset.variantId;
      const variantOption = this.variantSelect.querySelector(`option[value="${variantId}"]`);
      let noSellingPlans = false;
      if(!variantOption) return; // no variant option somehow? return.
      // update the variant select with latest variant info.
      this.variantSelect.value = variantId;
      this.skuInput.value = variantOption.dataset.sku;
      // if this product offers selling plans, make sure we switch over to the most relevant selling plan for the variant.
      if(this.sellingPlanSelect){
        // update the selling plan radios with prices that reflect the selected variant.
        const variantSellingPlans = this.sellingPlanSelect.querySelector(`optgroup[data-variant-id="${this.variantSelect.value}"]`);
        if(!variantSellingPlans){
          // console.log("Unable to find selling plans for the given variant.");
          this.sellingPlanSelect.value = ""; // clear the value, aka no selling plan.
          return;
        } else if (variantSellingPlans.hasAttribute('data-no-selling-plans')) {
          noSellingPlans = true;
          // console.log("Recently selected variant is not meant to offer selling plans.");
        }
        let selectedSellingPlanOption = this.sellingPlanSelect.querySelector(`optgroup[data-variant-id="${this.variantSelect.value}"] option[value="${this.sellingPlanSelect.value}"]`);
        if(!selectedSellingPlanOption){
          // if we cannot find a selling plan under the variant id and existing plan id (selling plan ids are shared across all variants), then select "onetime purchase"
          // console.log("The previously selected selling plan is not available for this variant, selecting onetime purchase.");
          selectedSellingPlanOption = this.sellingPlanSelect.querySelector(`optgroup[data-variant-id="${this.variantSelect.value}"] option[data-onetime-purchase]`);
        }
        if(selectedSellingPlanOption){
          // console.log("variant starting on selling plan: ", selectedSellingPlanOption);
          this.sellingPlanSelect.value = selectedSellingPlanOption.value; // if the selected plan is onetime purchase, this will clear the selling plan.
        }
        // go through every selling plan radio element on the card (top form, and mini form) and make sure values are updated to reflect the current variant.
        this.sellingPlanRadios.forEach((sellingPlanRadio)=>{
          const sellingPlanOption = variantSellingPlans.querySelector(`option[value="${sellingPlanRadio.dataset.planId}"]`);
          const radioContainer = sellingPlanRadio.closest('.product-card__selling-plan');
          const radioLabel = radioContainer.querySelector('label');
          const sellingPlanPrice = radioLabel.querySelector('.selling-plan__price');
          // if we can find the selling plan <option>, and the label renders the option price, update it.
          if(sellingPlanOption && sellingPlanPrice){
            sellingPlanPrice.innerHTML = sellingPlanOption.dataset.renderPrice;
          }
          // when evaluating the selling plan radios, check if the variant itself supports plans, and make adjustments depending.
          /*
            If the variant doesn't support selling plans, and the evaluated radio is NOT onetime, disable and hide it.
            If the variant doesn't support selling plans, and the evaluated radio is onetime, make sure we select it.
            If the variant does support selling plans, no matter what make sure we enable and show the selling plan.
          */
          if(noSellingPlans && sellingPlanRadio.value != "onetime"){
            // disable the radio
            sellingPlanRadio.disabled = true;
            sellingPlanRadio.checked = false;
            // hide the option container
            radioContainer.style.display = "none";
            // console.log("Variant is not meant for selling plans, disabling selling plan radio.");
          } else if(noSellingPlans && sellingPlanRadio.value == "onetime"){
            // make sure onetime is selected
            sellingPlanRadio.checked = true;
            // console.log("Variant is not meant for selling plans, setting onetime to the selected selling radio.");
          } else if(noSellingPlans == false){
            // enable the radio
            sellingPlanRadio.disabled = false;
            // show the option container
            radioContainer.style.display = "block";
            // console.log("Variant is meant for selling plans, enabling selling plan radio.");
          }
        });
      }
      this.updateAddToCart();

    }

    handleSubscriptionChange(input){
      // console.log(`ProductCard handleSubscriptionChange()`, input);
      /*
        This function will handle updating the value of the sellingPlanSelect element.
        The event listener will send the event target to this function, search for an <option> element in the sellingPlanSelect that matches the selling plan ID of the event target.
        Selling plans are grouped by variants, and generally the selling plan ids are the same between the variants, but if that ever changes, we want to make sure we are selecting the selling plan under the current variant.
      */
      if(!input) return;
      const sellingPlanId = input.dataset.planId;
      let sellingPlanOption = this.sellingPlanSelect.querySelector(`optgroup[data-variant-id="${this.variantSelect.value}"] option[value="${sellingPlanId}"]`);
      if(!sellingPlanOption){
        // if we cannot find a selling plan, select the onetime price option.
        sellingPlanOption = this.sellingPlanSelect.querySelector(`optgroup[data-variant-id="${this.variantSelect.value}"] option[data-onetime-purchase]`);
      }
      if(sellingPlanOption){
        this.sellingPlanSelect.value = sellingPlanId;
        this.updateAddToCart();
      } else {
        // console.log(`Unable to determine selling plan for variant ${this.variantSelect.value} selling plan: "${sellingPlanId}"`)
      }
    }

    // maniuplation functions
    updateAddToCart(){
      /*
        This function will manage the "add to bag" button state of the component.
        First, grab the current selected variant from the main variant select. If we can grab a variant, we want to grab the "render price" from the dataset.
        Set the "renderPrice" variable to this value.
        Next, check if this form / product has selling plans, if so, grab the current selected selling plan.
        If the "currently selected selling plan" is "onetime" the value will be "" and sellingPlanOption will be empty, that is okay because that price would match the selected variant's renderPrice anyway.
        If we are able to grab a "sellingPlanOption", this means a selling plan is selected and most likely we will need to display the selling plan's discount on the price.
        Grab the discounted price, and use that as the "renderPrice" value.
        Finally, check if the selected variant is available. If it is, edit the innerHTML of the submit button to be "[add to cart text] - [renderPrice]" otherwise render "[sold out text]"
       */
      const selectedVariantOption = this.variantSelect.querySelector(`option[value="${this.variantSelect.value}"]`);
      if(!selectedVariantOption) return;
      const selectedVariantAvailable = selectedVariantOption.dataset.available;
      const selectedVariantPrice = selectedVariantOption.dataset.renderPrice;
      let renderPrice = selectedVariantPrice;
      if(this.sellingPlanSelect){
        const sellingPlanOption = this.sellingPlanSelect.querySelector(`optgroup[data-variant-id="${this.variantSelect.value}"] option[value="${this.sellingPlanSelect.value}"]`);
        if(sellingPlanOption){
          renderPrice = sellingPlanOption.dataset.renderPrice;
        }
      }
      // update all ATC buttons on the component.
      if(this.submitButtons){
        this.submitButtons.forEach((submitButton)=>{
          let submitText = ``;
          if(!selectedVariantAvailable){
            submitText = `${theme.strings.soldOut}`;
            submitButton.disabled = true;
          } else {
            submitText = `${theme.strings.addToCart} - ${renderPrice}`;
            submitButton.disabled = false;
          }
          submitButton.innerHTML = `${submitText}`;
        });
      }
      if(this.cardPrice){
        this.cardPrice.innerHTML = renderPrice;
      }
    }

    // visibility functions
    openFormTop(){
      // console.log(`ProductCard openFormTop()`, this.formTop);
      if(!this.formTop) return;
      this.showFormElement(this.formTop);
      this.showFormElement(this.formTop.querySelector('button[type="submit"]'));
    }

    closeFormTop(){
      // console.log(`ProductCard closeFormTop()`, this.formTop);
      if(!this.formTop) return;
      this.hideFormElement(this.formTop);
      this.hideFormElement(this.formTop.querySelector('button[type="submit"]'));
    }

    openMiniForm(){
      // console.log(`ProductCard openMiniForm()`, this.miniForm);
      if(!this.miniForm) return;
      this.showFormElement(this.miniForm.querySelector('button[type="submit"]'));
      this.showFormElement(this.miniForm.querySelector('.product-options__container'));
      this.hideFormElement(this.miniForm.querySelector('button.product-card__button-select-options'));
      this.updateAddToCart();
    }

    closeMiniForm(){
      // console.log(`ProductCard closeMiniForm()`, this.miniForm);
      if(!this.miniForm) return;
      this.hideFormElement(this.miniForm.querySelector('button[type="submit"]'));
      this.hideFormElement(this.miniForm.querySelector('.product-options__container'));
      this.showFormElement(this.miniForm.querySelector('button.product-card__button-select-options'));
    }

    closeAllForms(){
      this.closeFormTop();
      this.closeMiniForm();
    }
  }

  customElements.define('product-card', ProductCard);
}
